import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

/**
 * HoverCard Component
 *
 * A portal-rendered informational card shown on hover, keyboard focus, or
 * tap/click. Fixed positioning so it escapes overflow containers (tables,
 * scroll areas). The visual card is presentational; screen readers get the
 * same content from an always-mounted hidden description on the trigger, so
 * aria-describedby is announced reliably on focus.
 *
 * @example
 * <HoverCard content={<FlightDetails flights={flights} />}>
 *   <span>CLT → ALB</span>
 * </HoverCard>
 */

const CARD_GAP = 8;
const CARD_WIDTH = 288;
const CARD_MAX_HEIGHT = 260;
const CLOSE_GRACE_MS = 120;

const HoverCard = ({ content, disabled = false, className, children }) => {
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);
  const [position, setPosition] = useState(null);
  const tooltipId = useId();

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const open = useCallback(() => {
    if (disabled || !triggerRef.current) return;
    cancelClose();
    const rect = triggerRef.current.getBoundingClientRect();
    const placeAbove =
      rect.bottom + CARD_GAP + CARD_MAX_HEIGHT > window.innerHeight &&
      rect.top > CARD_MAX_HEIGHT;
    setPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - CARD_WIDTH - 8)),
      ...(placeAbove
        ? { bottom: window.innerHeight - rect.top + CARD_GAP }
        : { top: rect.bottom + CARD_GAP }),
    });
  }, [disabled, cancelClose]);

  const close = useCallback(() => {
    cancelClose();
    setPosition(null);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setPosition(null), CLOSE_GRACE_MS);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    if (!position) return undefined;
    const onDismiss = () => close();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    const onDocumentClick = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) close();
    };
    window.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onDocumentClick);
    return () => {
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onDocumentClick);
    };
  }, [position, close]);

  return (
    <span
      ref={triggerRef}
      className={clsx(
        'relative rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wrg-blue-500',
        className,
      )}
      tabIndex={disabled ? undefined : 0}
      aria-describedby={disabled ? undefined : tooltipId}
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
      onFocus={open}
      onBlur={close}
      onClick={() => {
        if (!position) open();
      }}
    >
      {children}
      {!disabled && (
        <span id={tooltipId} className="sr-only">
          {content}
        </span>
      )}
      {position &&
        createPortal(
          <div
            aria-hidden="true"
            style={{ ...position, width: CARD_WIDTH, maxHeight: CARD_MAX_HEIGHT }}
            className="fixed z-90 max-w-[90vw] overflow-y-auto rounded-lg bg-white p-3 shadow-soft-lg ring-1 ring-gray-200 animate-tooltip-in motion-reduce:animate-none"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {content}
          </div>,
          document.body,
        )}
    </span>
  );
};

export default HoverCard;
export { HoverCard };
